import {Component, DOCUMENT, Inject, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MemoryService} from '../../../../core/services/memory.service';
import {LogoComponent} from '../../../../shared/components/logo/logo.component';
import {MatDivider} from '@angular/material/divider';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {DateTime} from 'luxon';
import {CourseService} from '../../course.service';
import {GroupWeeklyRequestDto} from '../../dtos/groupweeklyrequest.dto';
import {GroupScheduleDto} from '../../dtos/groupschedule.dto';
import {MatIconModule} from '@angular/material/icon';
import {GroupSeriesAppointmentDto} from '../../dtos/groupseriesappointment.dto';
import {CourseInfoDialog} from '../../dialogs/course-info/course-info.dialog';
import {MatDialog} from '@angular/material/dialog';
import {BreakpointObserver} from '@angular/cdk/layout';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorService} from '../../../../core/services/error.service';
import {MatDatepickerInputEvent, MatDatepickerModule} from '@angular/material/datepicker';
import {provideLuxonDateAdapter} from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-course-schedule',
  imports: [
    MatIconModule,
    LogoComponent,
    MatDivider,
    TranslocoPipe,
    IconButtonComponent,
    MatDatepickerModule
  ],
  providers: [
    provideLuxonDateAdapter()
  ],
  templateUrl: './course-schedule.component.html',
  styleUrl: './course-schedule.component.scss'
})
export class CoursesScheduleComponent implements OnInit {
  router = inject(Router);
  memoryService = inject(MemoryService);
  courseService = inject(CourseService);
  translocoService = inject(TranslocoService);
  errorService = inject(ErrorService);
  dialog: MatDialog = inject(MatDialog);
  mode: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  currentDay = DateTime.now().startOf("day");
  startDate = DateTime.now().startOf("week");
  endDate = DateTime.now().startOf("week").plus({days: 6});
  activeSchedule?: GroupScheduleDto;
  weekInfo?: string;
  allCourses?: GroupScheduleDto[];

  constructor(@Inject(DOCUMENT) private document: Document, private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe([
      '(max-width: 1199px)',     // mobile
      '(min-width: 1200px)'      // desktop
    ]).subscribe(result => {
      if (result.breakpoints['(max-width: 1199px)']) {
        this.mode = 'mobile';
      } else if (result.breakpoints['(min-width: 1200px)']) {
        this.mode = 'desktop';
      }
    });
  }

  ngOnInit() {
    this.refreshCourses();
  }

  changeWeek(delta: number) {
    this.startDate = this.startDate?.plus({week: delta});
    this.endDate = this.startDate.plus({days: 6});
    this.currentDay = this.startDate;
    this.refreshCourses();
  }

  changeDay(delta: number) {
    this.currentDay = this.currentDay?.plus({days: delta});
    if ((delta < 0 && this.startDate > this.currentDay) || (delta > 0 && this.endDate < this.currentDay)) {
      this.startDate = this.startDate?.plus({week: delta});
      this.endDate = this.startDate.plus({days: 6});
      this.refreshCourses();
    } else {
      this.refreshDayLists();
    }
  }

  onDaySelected(event: MatDatepickerInputEvent<DateTime>) {
    const selectedDay = event.value?.startOf('day');
    if (!selectedDay || !selectedDay.isValid) {
      return;
    }

    this.currentDay = selectedDay;
    const newStartOfWeek = this.currentDay.startOf('week');
    if (!newStartOfWeek.equals(this.startDate)) {
      this.startDate = newStartOfWeek;
      this.endDate = this.startDate.plus({days: 6});
      this.refreshCourses();
    } else {
      this.refreshDayLists();
    }
  }

  refreshCourses() {
    this.weekInfo = `${this.startDate.toFormat('dd.MM.yyyy')} - ${this.endDate.toFormat('dd.MM.yyyy')}`;
    const payload: GroupWeeklyRequestDto = {
      locationId: this.memoryService.locationId,
      customerId: this.memoryService.customerId,
      patientId: this.memoryService.patientId,
      startOfWeek: this.startDate.toISODate()
    };

    this.courseService.loadWeeklyCourses(payload).subscribe({
      next: (dtos: GroupScheduleDto[]) => {
        this.allCourses = dtos;
        this.refreshDayLists();
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.courses'));
      }
    });
  }

  refreshDayLists() {
    this.activeSchedule = this.allCourses?.find(p => DateTime.fromISO(p.day!).toFormat('dd.MM.yyyy') == this.currentDay.toFormat('dd.MM.yyyy'));
  }

  getFormatedDate(input: GroupScheduleDto) {
    if (!input) {
      return "-";
    }

    var startTime = DateTime.fromISO(input?.day!);
    const weekday = startTime.setLocale(this.translocoService.getActiveLang()).toFormat('EEE');
    const date = startTime.toFormat('dd.MM.');
    return `${weekday} ${date}`;
  }

  getFormatedDateLong() {
    const weekday = this.currentDay.setLocale(this.translocoService.getActiveLang()).toFormat('cccc');
    const date = this.currentDay.toFormat('dd.MM.yyyy');
    return `${weekday} ${date}`;
  }

  getStartDurationInfo(input: GroupSeriesAppointmentDto) {
    if (!input) {
      return "-";
    }

    var startTime = DateTime.fromISO(input?.start!);
    var duration = DateTime.fromISO(input?.end!).diff(startTime, 'minutes').minutes;
    return `${startTime.toFormat('HH:mm')} | ${duration} Min.`;
  }

  onHover(input: GroupSeriesAppointmentDto) {
    this.allCourses?.forEach((l: GroupScheduleDto) => {
      l.morningAppointments?.forEach(p => {
        if (p.groupSeries == input.groupSeries) {
          p.isHighlighted = true;
        }
      });
      l.dayAppointments?.forEach(p => {
        if (p.groupSeries == input.groupSeries) {
          p.isHighlighted = true;
        }
      });
      l.eveningAppointments?.forEach(p => {
        if (p.groupSeries == input.groupSeries) {
          p.isHighlighted = true;
        }
      });
    });
  }

  onLeave() {
    this.allCourses?.forEach((l: GroupScheduleDto) => {
      l.morningAppointments?.forEach(p => {
        p.isHighlighted = false;
      });
      l.dayAppointments?.forEach(p => {
        p.isHighlighted = false;
      });
      l.eveningAppointments?.forEach(p => {
        p.isHighlighted = false;
      });
    });
  }

  showBooking(input: GroupSeriesAppointmentDto) {
    this.dialog.open(CourseInfoDialog, {autoFocus: false, maxHeight: 'calc(100vh - 48px)', data: {course: {id: input.groupSeries}}});
  }
}
